import { CheckBox } from '@bambi/component-library/src/components/inputs/CheckBox/CheckBox';
import { ChevronDownIcon, Cross1Icon } from '@radix-ui/react-icons';
import * as Popover from '@radix-ui/react-popover';
import { useMemo, useRef, useState } from 'react';

export interface PrimaryMultiSelectOption<T extends string = string> {
  value: T;
  label: string;
  hint?: string;
}

export interface PrimaryMultiSelectValue<T extends string = string> {
  selected: T[];
  primary: T | '';
}

export interface PrimaryMultiSelectProps<T extends string = string> {
  id?: string;
  options: PrimaryMultiSelectOption<T>[];
  selected: T[];
  primary?: T | '';
  onChange: (next: PrimaryMultiSelectValue<T>) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  primaryLabel: string;
  primaryBadge: string;
  emptyText: string;
  toggleLabel: string;
  testIdPrefix: string;
  allWhenEmpty?: boolean;
}

export function PrimaryMultiSelect<T extends string = string>({
  id,
  options,
  selected,
  primary = '',
  onChange,
  placeholder,
  disabled,
  error,
  primaryLabel,
  primaryBadge,
  emptyText,
  toggleLabel,
  testIdPrefix,
  allWhenEmpty,
}: PrimaryMultiSelectProps<T>) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const controlRef = useRef<HTMLDivElement>(null);

  const optionsByValue = useMemo(
    () => new Map(options.map((option) => [option.value, option])),
    [options]
  );

  const selectedOptions = selected
    .map((value) => optionsByValue.get(value))
    .filter((option): option is PrimaryMultiSelectOption<T> => Boolean(option));

  const visibleOptions = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(trimmed) ||
        option.hint?.toLowerCase().includes(trimmed)
    );
  }, [options, query]);

  const showAllAsPrimaryChoices =
    Boolean(allWhenEmpty) && selected.length === 0;

  const chipOptions =
    showAllAsPrimaryChoices && primary
      ? [optionsByValue.get(primary)].filter(
          (o): o is PrimaryMultiSelectOption<T> => Boolean(o)
        )
      : selectedOptions;

  const primaryChoices = showAllAsPrimaryChoices ? options : selectedOptions;

  const toggle = (value: T) => {
    if (selected.includes(value)) {
      const nextSelected = selected.filter((v) => v !== value);
      let nextPrimary = primary;

      if (primary === value) {
        if (allWhenEmpty && nextSelected.length === 0) {
          // Empty selection means "all offered" when allWhenEmpty is on, so
          // the removed value is still a valid default. Mirrors the
          // first-pick-keeps-the-default rule below.
          nextPrimary = primary;
        } else {
          nextPrimary = nextSelected.length === 1 ? nextSelected[0] : '';
        }
      }

      onChange({ selected: nextSelected, primary: nextPrimary });
      return;
    }

    if (showAllAsPrimaryChoices && primary && primary !== value) {
      onChange({ selected: [primary, value], primary });
      return;
    }

    onChange({
      selected: [...selected, value],
      primary: primary || value,
    });
  };

  const removeChip = (value: T) => {
    if (!selected.includes(value)) {
      // lone primary chip shown in allWhenEmpty mode
      onChange({ selected, primary: '' });
      return;
    }
    toggle(value);
  };

  return (
    <div className="w-full">
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Anchor asChild>
          <div
            ref={controlRef}
            className={`relative flex w-full flex-wrap items-center gap-1 rounded-md border bg-white py-2 px-3 pr-8 text-left sm:text-sm ${
              error ? 'border-red-500' : 'border-gray-300'
            } ${disabled ? 'cursor-not-allowed bg-gray-50' : ''}`}
            data-testid={id ? `${id}-control` : `${testIdPrefix}-control`}
          >
            {chipOptions.map((option) => (
              <span
                key={option.value}
                className={`flex items-center gap-1 truncate rounded-md border px-2 py-1 text-sm ${
                  primary === option.value ? 'border-mint' : 'border-gray-300'
                }`}
                data-testid={`${testIdPrefix}-chip-${option.value}`}
              >
                {option.label}
                {primary === option.value && (
                  <span
                    className="text-xs text-mint"
                    data-testid={`${testIdPrefix}-primary-badge-${option.value}`}
                  >
                    {primaryBadge}
                  </span>
                )}
                <button
                  type="button"
                  aria-label={`Remove ${option.label}`}
                  disabled={disabled}
                  onClick={() => removeChip(option.value)}
                  className="text-gray-500 hover:text-gray-900 disabled:cursor-not-allowed"
                >
                  <Cross1Icon className="h-3 w-3" />
                </button>
              </span>
            ))}

            <input
              id={id}
              type="text"
              className="min-w-[8rem] flex-1 border-0 p-0 text-sm focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-gray-50"
              placeholder={placeholder}
              value={query}
              disabled={disabled}
              onFocus={() => setOpen(true)}
              onChange={(event) => {
                setQuery(event.target.value);
                setOpen(true);
              }}
              data-testid={`${testIdPrefix}-filter`}
            />

            <Popover.Trigger asChild>
              <button
                type="button"
                aria-label={toggleLabel}
                disabled={disabled}
                className="absolute inset-y-0 right-0 flex items-center pr-2 disabled:cursor-not-allowed"
                data-testid={`${testIdPrefix}-toggle`}
              >
                <ChevronDownIcon className="h-4 w-4 text-primary-700" />
              </button>
            </Popover.Trigger>
          </div>
        </Popover.Anchor>

        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={4}
            className="z-20 w-[var(--radix-popover-trigger-width)] rounded-md border border-gray-300 bg-white text-sm shadow-lg"
            onOpenAutoFocus={(event) => event.preventDefault()}
            onInteractOutside={(event) => {
              if (controlRef.current?.contains(event.target as Node)) {
                event.preventDefault();
              }
            }}
            data-testid={`${testIdPrefix}-panel`}
          >
            {primaryChoices.length > 0 && (
              <div
                className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-gray-50 p-2"
                data-testid={`${testIdPrefix}-primary-bar`}
              >
                <span className="text-xs text-gray-500">{primaryLabel}</span>
                {primaryChoices.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={primary === option.value}
                    onClick={() =>
                      onChange({ selected, primary: option.value })
                    }
                    className={`rounded-full border px-3 py-1 text-xs ${
                      primary === option.value
                        ? 'border-mint bg-mint text-white'
                        : 'border-gray-300 bg-white text-gray-900 hover:bg-gray-100'
                    }`}
                    data-testid={`${testIdPrefix}-primary-pill-${option.value}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}

            <div className="max-h-60 overflow-auto py-1">
              {visibleOptions.map((option) => {
                const checkboxId = `${testIdPrefix}-option-${option.value}`;
                return (
                  <div
                    key={option.value}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50"
                  >
                    <CheckBox
                      id={checkboxId}
                      className="border-mint data-[state=checked]:bg-mint-100"
                      checked={selected.includes(option.value)}
                      onCheckedChange={() => toggle(option.value)}
                    />
                    <label
                      htmlFor={checkboxId}
                      className="flex-1 cursor-pointer text-gray-900"
                    >
                      {option.label}
                    </label>
                    {option.hint && (
                      <span className="text-gray-400">{option.hint}</span>
                    )}
                  </div>
                );
              })}

              {visibleOptions.length === 0 && (
                <p className="px-3 py-2 text-gray-500">{emptyText}</p>
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
